import React, { Suspense, useContext, useState, useCallback } from "react";

import { Tagline } from "../../../components/Tagline/Tagline";
import { Headline } from "../../../components/Headline";
import { ImageWithGrid } from "../../../components/Image";
import { generateClassString as gcs } from "../../../lib/helper/generateClassString";

import * as styles from "./component.module.less";
import ElementContext from "../../../components/ElementContext";
import { Media } from "../../../components/Media/Media";

import { useIsMobile, useIsDesktop } from "../../../lib/hooks/mediaQueries";
import GeneralPageContext from "../../../templates/GeneralPageContext";
import { useMediaQuery } from "react-responsive";

const MediaGallery = React.lazy(() =>
    import("../../../components/media-gallery/MediaGallery").then((module) => {
        return { default: module.MediaGallery };
    })
);

const MEDIA_LAYOUT_STANDARD = "standard";

export function NestedImage({
    data: {
        headline,
        tagline,
        hideImageDescription,
        semanticOfHeadline,
        mediaDescription,
        hideGrid,
        mediaTwoColumn,
        previewTwoColumn,
        mediaFullWidth,
        previewFullWidth
    },
    withGallery: mediaLayout,
    hasLightBackground = true
}) {
    const withGallery = mediaLayout === MEDIA_LAYOUT_STANDARD;

    const { column } = useContext(ElementContext);
    const isMobile = useIsMobile();
    const isDesktop = useIsDesktop();
    const isMax1600 = useMediaQuery({ query: "(max-width: 1600px)" });

    const {
        layout: { showAnchorNavigation }
    } = useContext(GeneralPageContext);

    const media = replaceImageDimensions(
        column === 2 ? mediaTwoColumn : mediaFullWidth
    );
    const customPreview = replaceImageDimensions(
        column === 2 ? previewTwoColumn : previewFullWidth
    );

    const description = mediaDescription || media?.title;

    const [galleryOpen, setGalleryOpen] = useState(false);

    const toggleGallery = useCallback(() => {
        setGalleryOpen((currentState) => !currentState);
    }, []);

    return (
        <div
            className={gcs(
                styles.mediaTextAdvancedContainer,
                column === 1
                    ? media.isImage
                        ? styles.fullWidthImage
                        : styles.fullWidthVideo
                    : ""
            )}
        >
            {tagline && (
                <Tagline hasLightBackground={hasLightBackground}>
                    {tagline}
                </Tagline>
            )}

            {headline && (
                <Headline
                    semanticOfHeadline={semanticOfHeadline}
                    hasLightBackground={hasLightBackground}
                >
                    {headline}
                </Headline>
            )}

            {media.isImage ? (
                <div className={styles.imageContainer}>
                    {withGallery && (
                        <button
                            className={gcs([
                                true,
                                styles.buttonForImage,
                                styles.buttonForVideo
                            ])}
                            onClick={() => toggleGallery(0)}
                        />
                    )}
                    <ImageWithGrid
                        gridSize={
                            (column === 1 && isMobile) ||
                            (column === 2 && isDesktop) ||
                            (showAnchorNavigation && column === 2 && isMax1600)
                                ? "s"
                                : "m"
                        }
                        image={media}
                        className={styles.imageWrapper}
                        gridDisable={hideGrid}
                        objectFit="contain"
                    />
                </div>
            ) : (
                <>
                    {withGallery && (
                        <button
                            className={gcs([
                                false,
                                styles.buttonForImage,
                                styles.buttonForVideo
                            ])}
                            onClick={() => toggleGallery(0)}
                        />
                    )}
                    <Media
                        mediaElement={{ media, customPreview }}
                        videoControls={true}
                        gridDisable={hideGrid}
                        gridSize={
                            (column === 1 && isMobile) ||
                            (column === 2 && isDesktop) ||
                            (showAnchorNavigation && column === 2 && isMax1600)
                                ? "s"
                                : "m"
                        }
                    />
                </>
            )}

            {!hideImageDescription && description && (
                <div className={styles.mediaTextAdvancedImageDesc}>
                    {description}
                </div>
            )}
            <Suspense fallback={<div>Loading...</div>}>
                {withGallery && galleryOpen && (
                    <MediaGallery
                        galleryImages={[{ media: media }]}
                        thumbnails={[{ media: media }]}
                        toggleFunction={toggleGallery}
                        initialElement={0}
                    />
                )}
            </Suspense>
        </div>
    );
}

function replaceImageDimensions(media) {
    const returnMedia = media;

    if (
        media.width < media?.gatsbyImageData?.width &&
        media.height < media?.gatsbyImageData?.height
    ) {
        returnMedia.gatsbyImageData.width = media.width;
        returnMedia.gatsbyImageData.height = media.height;
    }

    return returnMedia;
}
